<template>
  <div class="page-content">
    <div class="absolute-fullsize" v-if="loading" v-loading="loading"></div>
    <template v-else>
      <h1>{{ title }}</h1>

      <v-section top-indent>
        <v-toolbar>
          <v-button theme="danger" :disabled="isButtonDisabled" @click="handleClick">{{ button }}</v-button>
        </v-toolbar>

        <hr />

        <div class="table-wrapper">
          <v-action-log-ads-table
            v-loading="loading"
            ref="adsTable"
            :data="list"
            :default-sort="sort"
            :cpl="accountCPL"
            :empty-text="$t('c.table.textEmpty')"
            @sort-change="handleChangeSort"
            @selection-change="selection = $event"
          />

          <v-row justify="center" class="pagination-wrapper" v-if="totalPage && totalPage > 1">
            <v-pagination
              layout="prev, pager, next"
              :page-size="1"
              :current-page="currentPage"
              :total="totalPage"
              :disabled="loading"
              @current-change="handleChangeCurrentPage"
            />
          </v-row>
        </div>
      </v-section>
    </template>
  </div>
</template>

<script>
import VActionLogAdsTable from '@/components/ActionLog/VActionLogAdsTable.vue'
import VPagination from '@/components/common/VPagination.vue'
import VToolbar from '@/components/common/VToolbar.vue'
import VSection from '@/components/common/VSection.vue'
import VRow from '@/components/common/VRow.vue'
import * as types from '@/store/types'
import * as authorities from '@/utils/authorities'
import storeUtils from '@/mixins/storeUtils'
import { templates } from '@/utils/notification'
import * as logTypes from '@/constants/logTypes'

export default {
  name: 'ActionLogAds',

  components: {
    VActionLogAdsTable,
    VPagination,
    VToolbar,
    VSection,
    VRow
  },

  mixins: [storeUtils],

  data() {
    return {
      logTypes,
      type: types.ACTION_LOG_ADS,
      selection: []
    }
  },

  computed: {
    accountCPL() {
      return this.$store.getters.accountCPL
    },
    loading() {
      return this.$store.state[this.type].loading
    },
    list() {
      return this.$store.state[this.type].list
    },
    sort() {
      return this.$store.state[this.type].params.sort
    },
    currentPage() {
      return this.$store.state[this.type].params.currentPage
    },
    totalPage() {
      return this.$store.state[this.type].params.totalPage
    },
    logType() {
      return this.$store.state[this.type].model.type
    },
    title() {
      if (
        [logTypes.ADS_WHITELIST_SWITCH, logTypes.ADS_WHITELIST_ADD].includes(
          this.logType
        )
      ) {
        return this.$t('v.actionLogAds.titleAddedToExceptions')
      }
      if (this.logType === logTypes.ADS_WHITELIST_DELETE) {
        return this.$t('v.actionLogAds.titleRemovedFromExceptions')
      }
      if (this.logType === logTypes.ADS_PAUSE) {
        return this.$t('v.actionLogAds.titleDisabled')
      }
      if (this.logType === logTypes.ADS_WORST_AUTOPAUSE) {
        return this.$t('v.actionLogAds.titleAutodisabled')
      }

      // logType == enable_ads
      return this.$t('v.actionLogAds.titleEnabled')
    },
    button() {
      if (
        [logTypes.ADS_WHITELIST_SWITCH, logTypes.ADS_WHITELIST_ADD].includes(
          this.logType
        )
      ) {
        return this.$t('v.actionLogAds.buttonRemoveFromExceptions')
      }
      if (this.logType === logTypes.ADS_WHITELIST_DELETE) {
        return this.$t('v.actionLogAds.buttonAddToExceptions')
      }
      if (
        [logTypes.ADS_PAUSE, logTypes.ADS_WORST_AUTOPAUSE].includes(
          this.logType
        )
      ) {
        return this.$t('v.actionLogAds.buttonEnableAndAddToExceptions')
      }

      // logType == enable_ads
      return this.$t('v.actionLogAds.buttonDisable')
    },
    isButtonDisabled() {
      return !this.canChangeAdsStatus
    },
    canChangeAdsStatus() {
      return this.$store.getters.hasAuthorities(authorities.CHANGE_ADS_STATUS)
    }
  },

  created() {
    // reset to default values
    const model = {
      id: this.$route.params.id,
      type: null
    }
    const params = {
      sort: {
        prop: null,
        order: null
      },
      limit: 10,
      currentPage: 1,
      totalPage: null
    }
    const list = []

    this.setActionLogAdsModel(model)
    this.setActionLogAdsParams(params)
    this.setActionLogAdsList(list)
    this.fetchActionLogAds()
  },

  destroyed() {
    this.clearAdsLog(types.ACTION_LOG_ADS)
  },

  methods: {
    handleChangeCurrentPage(value) {
      this.updateValue(this.type, { key: 'currentPage', value })
      this.fetchActionLogAds()
    },

    handleChangeSort({ prop, order }) {
      const { prop: oldProp, order: oldOrder } = this.$store.state[
        this.type
      ].params.sort

      if (prop !== oldProp || order !== oldOrder) {
        this.updateValue(this.type, { key: 'sort', value: { prop, order } })
        this.fetchActionLogAds()
      }
    },

    handleClick() {
      if (
        [logTypes.ADS_WHITELIST_SWITCH, logTypes.ADS_WHITELIST_ADD].includes(
          this.logType
        )
      ) {
        this.changeExceptions('delete')
      } else if (
        [logTypes.ADS_PAUSE, logTypes.ADS_WORST_AUTOPAUSE].includes(
          this.logType
        )
      ) {
        this.enableAndAddToExceptions()
      } else if (this.logType === logTypes.ADS_WHITELIST_DELETE) {
        this.changeExceptions('add')
      } else {
        // logType == logTypes.ADS_ENABLE
        this.pauseAds()
      }
    },

    setActionLogAdsModel(model) {
      this.setModel(this.type, model)
    },

    setActionLogAdsList(list) {
      this.setList(this.type, list)
    },

    setActionLogAdsParams(params) {
      this.setParams(this.type, params)
    },

    updateValue(type, { key, value }) {
      const params = {
        [key]: value
      }

      this.updateParams(type, params)
    },

    fetchActionLogAds() {
      this.$store.dispatch(types.FETCH_ACTION_LOG_ITEM, { name: this.type })
    },

    pauseAds() {
      this.setAdsStatus('PAUSED')
    },

    enableAndAddToExceptions() {
      const { table } = this.$refs.adsTable.$refs
      const { type: name, selection } = this
      const criterions = selection
        .filter(item => item.adState !== 'disabled')
        .map(item => item.id)

      if (criterions.length) {
        this.$store
          .dispatch(types.CHANGE_ADS_STATUS_FROM_ACTION_LOG, {
            name,
            criterions
          })
          .then((result) => {
            this.notifyUserAboutChangedExceptionAds('toggle', result)
            table.clearSelection()
          })
      }
    },

    changeExceptions(action) {
      const { table } = this.$refs.adsTable.$refs
      const selection = this.selection || []
      const during = selection[0] && selection[0].during ? selection[0].during : null

      const ads = selection
        .filter(item => item.adState !== 'disabled')
        .map((item) => {
          if (action === 'add') {
            return item.adId
          }
          return item.id
        })

      if (ads.length) {
        this.$store
          .dispatch(types.CHANGE_EXCEPTION_ADS, {
            ads,
            during,
            action
          })
          .then((result) => {
            this.notifyUserAboutChangedExceptionAds(action, result)
            table.clearSelection()
          })
      }
    },

    setAdsStatus(status) {
      const { table } = this.$refs.adsTable.$refs
      const { type: name, selection } = this
      const ads = selection
        .filter(item => item.adState !== 'disabled')
        .map(item => item.adId)

      if (ads.length) {
        this.$store
          .dispatch(types.CHANGE_ADS_STATUS, { name, ads, status })
          .then((list) => {
            this.notifyUserAboutChangedAdsStatus(status, list)
            table.clearSelection()
          })
      }
    },

    notifyUserAboutChangedAdsStatus(status, adList) {
      if (adList.length) {
        this.$store.dispatch(types.PUSH_NOTIFICATION, {
          status,
          list: adList,
          template: templates.ADS_CHANGED_STATUS
        })
      }
    },

    notifyUserAboutChangedExceptionAds(action, result) {
      const { ads, count, logId } = result

      if (!ads.length) return

      const actionMap = {
        add: templates.ADS_ADDED_TO_EXCEPTIONS,
        delete: templates.ADS_REMOVED_FROM_EXCEPTIONS,
        toggle: templates.ADS_ENABLED_AND_ADDED_TO_EXCEPTIONS
      }

      const notificationTemplate = actionMap[action]

      this.$store.dispatch(types.PUSH_NOTIFICATION, {
        logId,
        count,
        list: ads,
        template: notificationTemplate
      })
    },

    clearAdsLog(name) {
      this.setList(name, [])
      this.setModel(name, {
        id: null,
        type: null
      })
      this.updateParams(name, {
        sort: {
          prop: null,
          order: null
        },
        currentPage: 1,
        totalPage: null
      })
    }
  }
}
</script>
